import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import {
  generalLedgerService,
  chartOfAccountsService,
  GLAccountDTO,
  CreateGLJournalLineRequest,
} from '../../../../services/accountingService';
import { useCurrencyFormat } from '../../../../hooks/useCurrency';
import { ArrowLeft, Plus, CircleX, Save } from 'lucide-react';
import { getServerErrorMessage } from '../../../../utils/errorUtils';

interface LineForm {
  accountId: number;
  debit: number;
  credit: number;
  description: string;
}

const emptyLine = (): LineForm => ({ accountId: 0, debit: 0, credit: 0, description: '' });

const JournalEntryCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fmt = useCurrencyFormat();

  const [accounts, setAccounts] = useState<GLAccountDTO[]>([]);
  const [entryDate, setEntryDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<LineForm[]>([emptyLine(), emptyLine()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    chartOfAccountsService.getAll(false).then((res) => {
      const data = (res?.data as any)?.data ?? res?.data ?? [];
      if (Array.isArray(data)) setAccounts(data);
    });
  }, []);

  const updateLine = (idx: number, patch: Partial<LineForm>) =>
    setLines((prev) => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (idx: number) => setLines((prev) => prev.length > 2 ? prev.filter((_, i) => i !== idx) : prev);

  const totalDebit = lines.reduce((s, l) => s + (l.debit || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (l.credit || 0), 0);
  const isBalanced = totalDebit > 0 && totalDebit === totalCredit;

  const handleSave = async () => {
    setError(null);
    const validLines = lines.filter((l) => l.accountId > 0 && (l.debit > 0 || l.credit > 0));
    if (validLines.length < 2) {
      setError(t('accounting.gl.needTwoLines'));
      return;
    }
    if (!isBalanced) {
      setError(t('accounting.gl.mustBalance', { debit: fmt(totalDebit), credit: fmt(totalCredit) }));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        entryDate,
        description: description.trim() || undefined,
        lines: validLines.map<CreateGLJournalLineRequest>((l) => ({
          accountId: l.accountId,
          debit: l.debit || 0,
          credit: l.credit || 0,
          description: l.description.trim() || undefined,
        })),
      };
      const res = await generalLedgerService.createManualEntry(payload);
      const created = res.data?.data;
      navigate(`/accounting/journal-entries/${created?.id ?? ''}`);
    } catch (err: any) {
      setError(getServerErrorMessage(err) ?? t('accounting.gl.failedToPost'));
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div style={{ padding: '0 20px 40px', maxWidth: 1000, margin: '0 auto' }}>
        <div className="ims-top-bar" style={{ marginBottom: 20 }}>
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/accounting/journal-entries')}>
              <ArrowLeft size={13} /> {t('accounting.gl.journalEntries')}
            </button>
            <div>
              <h1 className="ims-page-title">{t('accounting.gl.newEntry')}</h1>
              <p className="ims-page-subtitle">{t('accounting.gl.newEntrySubtitle')}</p>
            </div>
          </div>
          <div className="ims-top-bar-right">
            <button className="ims-btn ims-btn-primary" onClick={handleSave} disabled={saving || !isBalanced}>
              {saving ? <span className="ims-btn-spinner" /> : <Save size={16} strokeWidth={1.75} />}
              {t('accounting.gl.postEntry')}
            </button>
          </div>
        </div>

        {error && <div className="ims-alert ims-alert-error" style={{ marginBottom: 16 }}>{error}</div>}

        <div className="ims-card" style={{ marginBottom: 16 }}>
          <div className="ims-card-body">
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 16 }}>
              <div className="ims-form-group">
                <label className="ims-form-label">{t('accounting.ledger.date')}</label>
                <input type="date" className="ims-form-control" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} />
              </div>
              <div className="ims-form-group">
                <label className="ims-form-label">{t('common.description')}</label>
                <input type="text" className="ims-form-control" value={description} maxLength={500}
                  onChange={(e) => setDescription(e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        <div className="ims-card">
          <div className="ims-lines-header">
            <h2 className="ims-card-title">{t('accounting.gl.lines')}</h2>
            <button type="button" className="ims-btn ims-btn-secondary" onClick={addLine}>
              <Plus size={13} strokeWidth={2} /> {t('accounting.gl.addLine')}
            </button>
          </div>
          <div className="ims-table-scroll">
            <table className="ims-table">
              <thead>
                <tr>
                  <th className="ims-th">{t('accounting.coa.title')}</th>
                  <th className="ims-th">{t('common.description')}</th>
                  <th className="ims-th ims-th--num" style={{ width: 140 }}>{t('accounting.ledger.debit')}</th>
                  <th className="ims-th ims-th--num" style={{ width: 140 }}>{t('accounting.ledger.credit')}</th>
                  <th className="ims-th" style={{ width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, idx) => (
                  <tr key={idx} className="ims-tr">
                    <td className="ims-td">
                      <select className="ims-cell-input" value={l.accountId}
                        onChange={(e) => updateLine(idx, { accountId: Number(e.target.value) })}>
                        <option value={0}>{t('accounting.gl.selectAccount')}</option>
                        {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                      </select>
                    </td>
                    <td className="ims-td">
                      <input type="text" className="ims-cell-input" value={l.description}
                        onChange={(e) => updateLine(idx, { description: e.target.value })} />
                    </td>
                    <td className="ims-td">
                      <input type="number" className="ims-cell-input ims-cell-input--num" min={0} step="0.01" value={l.debit || ''}
                        onChange={(e) => updateLine(idx, { debit: parseFloat(e.target.value) || 0, credit: 0 })} />
                    </td>
                    <td className="ims-td">
                      <input type="number" className="ims-cell-input ims-cell-input--num" min={0} step="0.01" value={l.credit || ''}
                        onChange={(e) => updateLine(idx, { credit: parseFloat(e.target.value) || 0, debit: 0 })} />
                    </td>
                    <td className="ims-td">
                      <button type="button" className="ims-remove-btn" onClick={() => removeLine(idx)} disabled={lines.length <= 2}>
                        <CircleX size={18} color="#991b1b" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="ims-td" colSpan={2}><strong>{t('accounting.gl.total')}</strong></td>
                  <td className="ims-td ims-td--line-total"><strong>{fmt(totalDebit)}</strong></td>
                  <td className="ims-td ims-td--line-total"><strong>{fmt(totalCredit)}</strong></td>
                  <td className="ims-td" />
                </tr>
              </tfoot>
            </table>
          </div>
          {!isBalanced && totalDebit + totalCredit > 0 && (
            <div style={{ padding: '10px 16px', color: 'var(--ims-red-text)', fontSize: 13 }}>
              {t('accounting.gl.outOfBalance', { diff: fmt(Math.abs(totalDebit - totalCredit)) })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default JournalEntryCreatePage;
